import {
  documentTypeLabelToExportCode,
  isInvoiceFamilyCode,
} from '../utils/documentExportReference.ts';

export const MAIL_DISPATCH_PURPOSES = [
  'offer',
  'invoice',
  'dunning',
  'general',
  'work_report',
] as const;

export type MailDispatchPurpose = (typeof MAIL_DISPATCH_PURPOSES)[number];

export const MAIL_DISPATCH_DEFAULT_KEY = 'default' as const;

export type MailDispatchConfig = Record<MailDispatchPurpose, string | null> & {
  default: string | null;
  /** Send-from address for a purpose. Missing means the mailbox primary. */
  from: Partial<Record<MailDispatchPurpose | 'default', string | null>>;
};

export type MailDispatchSettings = Partial<MailDispatchConfig>;

export const MAIL_DISPATCH_PURPOSE_LABELS: Record<MailDispatchPurpose, string> = {
  offer: 'Angebote & Dokumente',
  invoice: 'Rechnungen',
  dunning: 'Mahnungen',
  general: 'Allgemein',
  work_report: 'Arbeitsberichte',
};

/** Kurzlabels für Toggle-Chips in der Postfach-Liste */
export const MAIL_DISPATCH_PURPOSE_SHORT_LABELS: Record<MailDispatchPurpose, string> = {
  offer: 'Angebote',
  invoice: 'Rechnungen',
  dunning: 'Mahnungen',
  general: 'Allgemein',
  work_report: 'Arbeitsberichte',
};

export function isMailDispatchPurpose(value: unknown): value is MailDispatchPurpose {
  return typeof value === 'string' && (MAIL_DISPATCH_PURPOSES as readonly string[]).includes(value);
}

export function emptyMailDispatchSettings(): MailDispatchConfig {
  return {
    offer: null,
    invoice: null,
    dunning: null,
    general: null,
    work_report: null,
    default: null,
    from: {},
  };
}

function parseFromAddress(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const email = value.trim().toLowerCase();
  return email || null;
}

function parseFromMap(raw: unknown): MailDispatchConfig['from'] {
  const from: MailDispatchConfig['from'] = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return from;
  const obj = raw as Record<string, unknown>;
  for (const purpose of MAIL_DISPATCH_PURPOSES) {
    const email = parseFromAddress(obj[purpose]);
    if (email) from[purpose] = email;
  }
  const fallback = parseFromAddress(obj[MAIL_DISPATCH_DEFAULT_KEY]);
  if (fallback) from.default = fallback;
  return from;
}

function parseAccountId(value: unknown): string | null {
  if (value == null || value === '') return null;
  return String(value).trim() || null;
}

export function parseMailDispatchSettings(raw: unknown): MailDispatchConfig {
  const base = emptyMailDispatchSettings();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return base;
  const obj = raw as Record<string, unknown>;
  for (const purpose of MAIL_DISPATCH_PURPOSES) {
    base[purpose] = parseAccountId(obj[purpose]);
  }
  base.default = parseAccountId(obj[MAIL_DISPATCH_DEFAULT_KEY]);
  base.from = parseFromMap(obj.from);
  return base;
}

export function serializeMailDispatchSettings(settings: MailDispatchConfig): Record<string, string | null | MailDispatchConfig['from']> {
  return {
    ...Object.fromEntries(MAIL_DISPATCH_PURPOSES.map((purpose) => [purpose, settings[purpose] ?? null])),
    [MAIL_DISPATCH_DEFAULT_KEY]: settings.default ?? null,
    from: settings.from ?? {},
  };
}

/** True when this address currently owns a Versandart or the fallback mailbox. */
export function addressMatchesDispatch(
  settings: MailDispatchConfig,
  purpose: MailDispatchPurpose | 'default',
  accountId: string,
  email: string,
  primary: string,
): boolean {
  const assignedId = purpose === 'default' ? settings.default : settings[purpose];
  if (String(assignedId ?? '') !== String(accountId)) return false;
  const stored = String(settings.from?.[purpose] ?? '').trim().toLowerCase();
  const wanted = String(email ?? '').trim().toLowerCase();
  if (!stored) return wanted === String(primary ?? '').trim().toLowerCase();
  return stored === wanted;
}

export function mailDispatchPurposeForDocumentLabel(documentTypeLabel: string): 'offer' | 'invoice' {
  const code = documentTypeLabelToExportCode(documentTypeLabel);
  return isInvoiceFamilyCode(code) ? 'invoice' : 'offer';
}

/**
 * Prefers the configured standard sender when it is in the sendable list.
 * Otherwise the first id (newest connected mailbox from the API).
 */
export function pickPreferredSendableInboxAccountId(
  accountIds: readonly string[],
  preferredAccountId?: string | null,
): string {
  const ids = accountIds.map((id) => String(id).trim()).filter(Boolean);
  if (ids.length === 0) return '';
  const preferred = String(preferredAccountId ?? '').trim();
  if (preferred && ids.includes(preferred)) return preferred;
  return ids[0] ?? '';
}
